import { machinesClient } from '../sdk/administrationClient.js';

import { defineAdministrationCollectionStore } from './administrationCollectionStore.js';

/**
 * Machines, as Platform Administration lists them: a page of programs, all or by status, one
 * program with its credentials and live tokens, and the writes — make one, suspend or resume
 * it, rotate or revoke a credential, revoke tokens, retire it.
 *
 * Two of the writes answer a client secret, once. Those two answer the server's reply to the
 * caller and hold none of it: a secret kept in a store outlives the dialog that showed it, and
 * the rule is that nothing can show it again. Every other write answers `true` or `false`, and
 * the held record is stale afterwards: `refresh` re-reads it.
 */
export const machinesStore = defineAdministrationCollectionStore({
    id: 'administration-machines',
    client: machinesClient,
    extend({ client, attempt }) {
        /** Answers `{ machineId, clientId, clientSecret, secretShownOnce }`, or `null` when refused. */
        async function create(name) {
            const attempted = await attempt(function () {
                return client.create({ name });
            });
            if (attempted.ok === false) {
                return null;
            }
            return attempted.value;
        }

        async function setStatus(machineId, status) {
            const attempted = await attempt(function () {
                return client.setStatus(machineId, status);
            });
            return attempted.ok;
        }

        /**
         * Answers `{ machineId, clientId, clientSecret, expiring, expiresInHours, secretShownOnce }`,
         * or `null` when refused. `options.expiresInHours` is how long the credentials being
         * replaced keep working; absent, the server's 24.
         */
        async function rotateCredential(machineId, options = {}) {
            const attempted = await attempt(function () {
                return client.rotateCredential(machineId, options);
            });
            if (attempted.ok === false) {
                return null;
            }
            return attempted.value;
        }

        async function revokeCredential(machineId, clientId) {
            const attempted = await attempt(function () {
                return client.revokeCredential(machineId, clientId);
            });
            return attempted.ok;
        }

        /** One token by `tokenId`, every token a secret made by `secretFingerprint`, or all of them. */
        async function revokeTokens(machineId, selection = {}) {
            const attempted = await attempt(function () {
                return client.revokeTokens(machineId, selection);
            });
            return attempted.ok;
        }

        async function retire(machineId) {
            const attempted = await attempt(function () {
                return client.retire(machineId);
            });
            return attempted.ok;
        }

        return { create, setStatus, rotateCredential, revokeCredential, revokeTokens, retire };
    }
});

export default machinesStore;
